<template>
	<div
		class="modal-bg"
		:class="[{ '--visible': isVisible }, { '--fixed': fixedHeaderFooter }]"
		@click.self="onBgClick"
		@scroll="onModalScroll"
	>
		<div
			ref="modal"
			:id="id"
			tabindex="-1"
			:role="isVisible ? 'dialog' : null"
			:aria-modal="isVisible ? true : null"
			:aria-labelledby="`${id}-modal-title`"
			:aria-describedby="`${id}-content`"
			:aria-label="title"
			class="modal"
			:class="[
				`modal--${modalSize}`,
				{ 'modal--closeable': closeable },
				{ 'modal--sticky-close-button': stickyCloseButton },
				{ 'modal--fixed-header-footer': fixedHeaderFooter },
				{ 'modal--release-footer': releaseFixedFooter },
				{ 'modal--scroll-bottom': !scrollBottom },
				{ 'mobile-safari': isMobileSafari }
			]"
		>
			<div
				ref="header"
				class="modal-header"
				:class="[
					{ 'modal-header--fixed': windowTop },
					{ 'modal-header--shadow': !scrollTop }
				]"
			>
				<div v-if="title" :id="`${id}-modal-title`" class="visually-hidden">
					<h2 class="visually-hidden" v-html="title" />
				</div>
				<button
					v-if="closeable"
					class="close-x ie-focusable"
					aria-label="Close modal window"
					ref="closeModalBtn"
					@click="onClose"
				>
					<span class="close-x__text p3 --bold">Close</span>
					<span class="icon icon-close close-x__icon" />
				</button>
			</div>

			<div
				:id="`${id}-content`"
				ref="content"
				class="container modal-content"
				@scroll="onContentScroll"
			>
				<component :is="contents" v-bind="dataObj" />
			</div>
		</div>
		<v-breakpoint v-model="model" />
	</div>
</template>

<script>
import { EventBus, VBreakpoint, Model } from "@vg-faset/core";
import { UniqueId } from "../../../utilities";

/**
 * The modal component is used to display a component within a modal box (also referred to as a dialog or popup).
 *
 * The modal component should **only exist once** in a page, so it should be included to the root component (ex: App.vue).
 *
 * EventBus is used as a go-between to communicate with the modal component.
 *
 * Note that you only need one Modal on a page; use modal:set to dynamically change the modal's contents.
 *
 * Modal can be displayed in a large (960px wide) or small (560px wide) format. Large format is displayed by default. To display small format, use the EventBus. See example below.
 *
 * Close button can be hidden by using the EventBus. This can be used to force the user to complete an action in order to close the modal. See example below.
 *
 * ## EventBus Modal Listeners
 *
 * Use the EventBus as a go-between to talk to this modal. Use `EventBus.$emit` to call a method within the modal.
 *
 * From whatever component triggers the modal:
 *
 * ```javascript
 * EventBus.$emit('modal:set', MyModalComponent); // Set the component that is displayed by the modal
 *
 * EventBus.$emit('modal:set-title', 'My Modal Title'); // This is used for accessibility with aria-labelledby
 *
 * EventBus.$emit('modal:set-data', {'myModalText': 'This is the text for my modal'}); //Optional. Keys must match props in component that modal is displaying
 *
 * EventBus.$emit('modal:set-closeable', true); // Optional. If not called, falls back to true. This can be used to hide close button and force the user to complete an action in order to close the modal.
 *
 * EventBus.$emit('modal:set-size', 'small'); //Optional. This can be set to 'small' to display small (560px wide) format instead of large (960px wide). If not called, falls back to 'large'.
 *
 * EventBus.$emit("modal:set-sticky-close-button", false); // Optional. When set to 'true, this sets the close button to be sticky at the top of the modal when scrolling.
 *
 * EventBus.$emit("modal:set-fixed-header-footer", false); // Optional. When set to 'true', this sets the modal to be fixed on the screen with sticky header and footers.
 *
 * EventBus.$emit('modal:show', e);
 * ```
 *
 * ## EventBus Modal Events
 *
 * Use `EventBus.$on` to listen for a method.
 *
 * ```javascript
 * EventBus.$on("modal:hide", this.onHide)
 * ```
 *
 */
export default {
	name: "Modal",
	components: { VBreakpoint },
	data() {
		return {
			model: new Model(),
			isVisible: false,
			contents: null,
			previousFocus: null,
			title: "Window",
			id: new UniqueId("modal-id-").generate(),
			closeable: true,
			modalSize: "large",
			dataObj: {},
			focusableEls: [],
			firstFocusableEnabledEl: null,
			lastFocusableEnabledEl: null,
			componentIsReady: false,
			stickyCloseButton: false,
			fixedHeaderFooter: false,
			releaseFixedFooter: false,
			windowTop: false,
			scrollTop: false,
			scrollBottom: false,
			isMobileSafari: false
		};
	},
	computed: {
		isSmall() {
			return this.model.isSmall;
		}
	},
	methods: {
		show() {
			// Store the last clicked link so we can return focus later
			if (document.activeElement) {
				this.previousFocus = document.activeElement;
			}
			document.body.classList.add("--modal-open");
			window.addEventListener("keyup", this.handleKeyup);
			window.addEventListener("keydown", this.handleKeydown);
			this.isVisible = true;
			this.onUpdate();
		},
		hide() {
			document.body.classList.remove("--modal-open");
			window.removeEventListener("keyup", this.handleKeyup);
			window.removeEventListener("keydown", this.handleKeydown);
			this.isVisible = false;
			this.componentIsReady = false;

			// Let other components know the modal has been hidden
			EventBus.$emit("modal:hidden", true);

			// Reset to default values when modal is closed for backward compatibility
			EventBus.$emit("modal:set-closeable", true);
			EventBus.$emit("modal:set-size", "large");

			// Return user focus to whatever they clicked to open this
			if (this.previousFocus && this.previousFocus.focus) {
				this.previousFocus.focus();
			}
		},
		setContents(component) {
			this.contents = component;
		},
		setTitle(title) {
			this.title = title;
		},
		setId(id) {
			this.id = id;
		},
		setCloseable(closeable) {
			this.closeable = closeable;
		},
		setSize(size) {
			this.modalSize = size;
		},
		setData(value) {
			this.dataObj = value;
		},
		setFocusableEls() {
			this.focusableEls = this.$refs.modal
				? this.$refs.modal.querySelectorAll(
						'a[href], area[href], input, select, textarea, button, [tabindex="0"]'
				  )
				: null;
			this.setFocusableEnabledEls();

			this.setFocus();
		},
		setFocusableEnabledEls() {
			this.setFirstFocusableEnabledEl();
			this.setLastFocusableEnabledEl();
		},
		setFirstFocusableEnabledEl() {
			for (const el of this.focusableEls) {
				if (!el.disabled && this.isTabbable(el)) {
					this.firstFocusableEnabledEl = el;
					break;
				}
			}
		},
		setLastFocusableEnabledEl() {
			for (let i = this.focusableEls.length; i >= 0; i--) {
				if (
					this.focusableEls[i] &&
					!this.focusableEls[i].disabled &&
					this.isTabbable(this.focusableEls[i])
				) {
					this.lastFocusableEnabledEl = this.focusableEls[i];
					break;
				}
			}
		},
		onComponentReady(componentName) {
			// This function is called with `EventBus.$emit('modal:component-ready', this.$options.name)`
			// from the component being loaded into the modal. When focusable fields become available
			// asynchronously, it re-polls the dom and updates the focusableEls array.
			// This will only run once.
			if (!this.componentIsReady && this.contents.name === componentName) {
				this.componentIsReady = true;
				this.setFocusableEls();
			}
		},
		setFocus() {
			if (this.firstFocusableEnabledEl) {
				if (
					this.isSmall ||
					this.firstFocusableEnabledEl === this.$refs.closeModalBtn
				) {
					// if in small viewport or if the first focusable enabled element is the close
					// button, put the focus on the modal. One tab gets you to the next focusable element.
					this.$refs.modal.focus();
				} else {
					this.firstFocusableEnabledEl.focus();
				}
			}
		},
		isTabbable(el) {
			let ret = true;

			//When tabbing to a group of radio buttons
			//if a radio button within the group is checked, the CHECKED radio button should become focused
			//If NO radio buttons within the group are checked, the FIRST radio button in the group should become focused
			if (el.getAttribute("type") === "radio" && el.name) {
				const inputSet = this.$refs.modal
					? this.$refs.modal.querySelectorAll(
							'input[type="radio"][name="' + el.name + '"]'
					  )
					: null;

				let checkedInput;
				for (var i = 0; i < inputSet.length; i++) {
					if (inputSet[i].checked) {
						checkedInput = inputSet[i];
					}
				}

				if (checkedInput) {
					ret = el === checkedInput;
				} else {
					ret = el === inputSet[0];
				}
			}
			return ret;
		},
		handleKeyup(e) {
			if ((e.key === "Escape" || e.key === "Esc") && this.closeable) {
				EventBus.$emit("modal:esc-key-pressed", true);
				this.hide();
			}
		},
		handleKeydown(e) {
			//Prevent focus from going outside modal. Improves accessibility.
			if (e.key === "Tab") {
				this.setFocusableEnabledEls();

				if (
					e.shiftKey &&
					this.firstFocusableEnabledEl === document.activeElement
				) {
					e.preventDefault();
					this.lastFocusableEnabledEl.focus();
				} else if (
					!e.shiftKey &&
					this.lastFocusableEnabledEl === document.activeElement
				) {
					e.preventDefault();
					this.firstFocusableEnabledEl.focus();
				}
			}
		},
		onClose() {
			EventBus.$emit("modal:close-button-clicked", true);
			this.hide();
		},
		onBgClick() {
			if (this.closeable) {
				EventBus.$emit("modal:bg-clicked", true);
				this.hide();
			}
		},
		setStickyCloseButton(value) {
			this.stickyCloseButton = value;
		},
		setFixedHeaderFooter(value) {
			this.fixedHeaderFooter = value;
		},
		setHeaderStyles() {
			if (this.$refs.modal) {
				this.$nextTick(() => {
					const resetHeaderStyleLeft = () => {
						this.$refs.header.style.left = null;
					};

					const resetHeaderStyleWidth = () => {
						this.$refs.header.style.width = null;
					};

					const setHeaderStyleWidth = () => {
						const modalWidth = this.$refs.modal.clientWidth;
						this.$refs.header.style.width = `${modalWidth}px`;
					};

					const setHeaderStyleLeft = () => {
						const modalLeft = this.$refs.modal.offsetLeft;
						this.$refs.header.style.left = `${modalLeft}px`;
					};

					if (this.isSmall || !this.windowTop) {
						resetHeaderStyleLeft();
					}

					if (this.isSmall) {
						setHeaderStyleWidth();
					} else if (this.windowTop && this.$refs.modal) {
						setHeaderStyleLeft();
						setHeaderStyleWidth();
					} else {
						resetHeaderStyleLeft();
						resetHeaderStyleWidth();
					}
				});
			}
		},
		onUpdate() {
			if (this.stickyCloseButton) {
				this.setHeaderStyles();
			} else if (this.fixedHeaderFooter) {
				this.setReleaseFixedFooter();
				this.onContentScroll(this.$refs.content);
			}

			// fallback to set tabbable elements if the modal:component-ready event
			// is not called from the component within the modal
			this.$nextTick(() => {
				this.setFocusableEls();
			});
		},
		onModalScroll(e) {
			const header = this.$refs.modal.offsetTop;
			const element = e.target ? e.target : e;
			this.windowTop = element.scrollTop > header;
		},
		onContentScroll(e) {
			const element = e.target ? e.target : e;
			this.scrollTop = element.scrollTop <= 0;
			this.scrollBottom =
				element.scrollHeight - element.clientHeight <= element.scrollTop;
		},
		setReleaseFixedFooter() {
			if (this.fixedHeaderFooter) {
				const contentHeight = this.$refs.content.clientHeight;
				const contentChildHeight = contentHeight
					? this.$refs.content.firstElementChild.clientHeight
					: 0;

				if (contentHeight && contentHeight > 0) {
					if (this.releaseFixedFooter) {
						this.releaseFixedFooter =
							!this.isSmall && contentHeight >= contentChildHeight - 1;
					} else {
						this.releaseFixedFooter =
							!this.isSmall && contentHeight >= contentChildHeight + 88;
					}
				}
			}
		},
		mobileSafariCheck() {
			this.isMobileSafari =
				[
					"iPad Simulator",
					"iPhone Simulator",
					"iPod Simulator",
					"iPad",
					"iPhone",
					"iPod"
				].includes(navigator.platform) ||
				(navigator.userAgent.includes("Mac") && "ontouchend" in document);
		}
	},
	mounted() {
		EventBus.$on("modal:set", this.setContents);
		EventBus.$on("modal:set-title", this.setTitle);
		EventBus.$on("modal:set-id", this.setId);
		EventBus.$on("modal:show", this.show);
		EventBus.$on("modal:hide", this.hide);
		EventBus.$on("modal:set-data", this.setData);
		EventBus.$on("modal:set-closeable", this.setCloseable);
		EventBus.$on("modal:set-size", this.setSize);
		EventBus.$on("modal:component-ready", this.onComponentReady);
		EventBus.$on("modal:set-sticky-close-button", this.setStickyCloseButton);
		EventBus.$on("modal:set-fixed-header-footer", this.setFixedHeaderFooter);

		if (window.innerWidth > 1024) {
			window.addEventListener("resize", this.onUpdate);
		} else {
			window.addEventListener("orientationchange", this.onUpdate);
		}

		this.mobileSafariCheck();
	},
	updated() {
		if (this.contents) {
			EventBus.$emit("modal:updated", this.contents);
		}

		if (this.componentIsReady && this.isVisible) {
			this.setFocusableEls();
		}

		if (this.stickyCloseButton) {
			this.setHeaderStyles();
		} else if (this.fixedHeaderFooter) {
			this.setReleaseFixedFooter();
			this.onContentScroll(this.$refs.content);
		}
	},
	beforeDestroy() {
		EventBus.$off("modal:set", this.setContents);
		EventBus.$off("modal:set-title", this.setTitle);
		EventBus.$off("modal:set-id", this.setId);
		EventBus.$off("modal:show", this.show);
		EventBus.$off("modal:hide", this.hide);
		EventBus.$off("modal:set-data", this.setData);
		EventBus.$off("modal:set-closeable", this.setCloseable);
		EventBus.$off("modal:set-size", this.setSize);
		EventBus.$off("modal:component-ready", this.onComponentReady);
		EventBus.$off("modal:set-sticky-close-button", this.setStickyCloseButton);

		if (window.innerWidth > 1024) {
			window.removeEventListener("resize", this.onUpdate);
		} else {
			window.removeEventListener("orientationchange", this.onUpdate);
		}
	}
};
</script>

<style lang="scss">
// Note:
// the Close X button styles have been moved to ui.scss
// because they are used in other non-modal components

.\--modal-open {
	overflow: hidden;
}

.modal-bg {
	display: none;

	&.\--visible {
		background: rgba($color--black, 0.7);
		bottom: 0;
		display: block;
		left: 0;
		overflow-x: hidden;
		overflow-y: auto;
		position: fixed;
		right: 0;
		top: 0;
		z-index: 201; // above everything including vg header!

		&.\--fixed {
			overflow-y: hidden;
		}
	}
}

.modal {
	background: $color--white;
	max-width: 100%;
	min-height: 100%;
	overflow: hidden;
	padding: 92px 0; // leaves room for the close button up top
	position: relative;
	outline: none;

	&.modal--small {
		padding: 40px;

		.container {
			margin: 0;
		}
	}

	@include to-md() {
		&.modal--small {
			margin: 27px;
			min-width: calc(100% - 54px);
			min-height: inherit;
		}
	}

	@include from-md() {
		margin: 32px;
		min-height: 0;
		padding: 92px 54px; // side padding = 80px when .container margins are added

		&.modal--small {
			margin: 32px auto;
			max-width: 560px;

			&.modal--closeable {
				max-width: 560px;
				padding: 80px 40px 40px;
			}
		}
	}

	@include from-lg() {
		margin: 80px auto;
		max-width: 960px;
		padding: 80px; // the .container margins are gone at this size

		&.modal--small {
			margin: 80px auto;
		}

		&.modal--closeable {
			max-width: 960px;
			padding: 92px 80px;
		}
	}

	.close-x {
		cursor: pointer;
	}

	.icon-close {
		padding: 16px;
	}

	.modal-content.container {
		@include to-md() {
			margin: 0 23px;
		}
	}

	.fas-core &.modal--sticky-close-button,
	&.modal--sticky-close-button {
		.modal-header {
			position: absolute;
			top: 0;
			right: 0;
			left: 0;
			height: 88px;
			background-color: $color--neutral-10;
			transition: box-shadow 0.25s ease;

			&--fixed {
				position: fixed;
				box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.66);

				@include from-md() {
					right: unset;
					left: 32px;
				}
			}
		}
	}

	.fas-core &.modal--fixed-header-footer,
	&.modal--fixed-header-footer {
		padding: 0 0 88px;

		&.modal--small {
			@include to-md() {
				margin: 0;
			}
		}

		.modal-header {
			position: relative;
			height: 88px;
			transition: all 0.25s ease;

			&--shadow {
				box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.66);
			}
		}

		.modal-content {
			margin-right: 0;
			margin-left: 0;
			overflow-y: auto;
			padding: 0 23px;
			// 176px = 88px * 2 (header/footer heights)
			height: calc(100vh - 176px);
			max-height: calc(100vh - 176px);

			@include from-md() {
				// 240px = (32px * 2) + (88px* 2) (top/bottom margin and header/footer heights)
				height: calc(100vh - 240px);
				max-height: calc(100vh - 240px);
				padding-left: 54px;
				padding-right: 54px;
				padding-bottom: 0;
			}

			@include from-lg() {
				// 336px = (80px * 2) + (88px * 2) top/bottom margin and header/footer heights
				height: calc(100vh - 336px);
				max-height: calc(100vh - 336px);
				padding-left: 80px;
				padding-right: 80px;
			}
		}

		&.mobile-safari {
			.modal-content {
				@include to-md() {
					padding: 0 23px 104px;
				}
			}
		}

		.close-x {
			top: 24px;
		}

		&.modal--release-footer {
			position: relative;
			padding-bottom: 24px;

			.modal-header--shadow {
				box-shadow: none;
			}

			.modal-content {
				position: relative;
				height: initial;
			}
		}
	}
}
</style>
